'use client';

import React from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import Icon from './FontAwesomeIcon';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'hover:opacity-90 focus:ring-blue-500',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500',
        outline: 'border-2 bg-transparent hover:bg-opacity-10 focus:ring-blue-500',
      },
      size: {
        small: 'text-sm px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

const Button = ({
  // Required parameters with defaults
  text = "Lihat Studi Kasus",
  text_font_size = "text-base",
  text_font_family = "Inter",
  text_font_weight = "font-medium",
  text_line_height = "leading-normal",
  text_text_align = "left",
  text_color = "text-text-white",
  fill_background_color = "bg-primary-background",
  border_border_radius = "rounded-sm",
  
  // Optional parameters (no defaults)
  border_border,
  effect_box_shadow,
  layout_gap,
  layout_width,
  padding,
  margin,
  position,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  leftImage,
  leftIcon,          // Can be: { class: "fa-solid fa-icon" } (legacy) OR Font Awesome icon definition
  leftIconColor,     // Tailwind color class for Font Awesome icons
  rightIcon,         // Font Awesome icon definition
  rightIconColor,    // Tailwind color class for right icon
  ...props
}) => {
  // Safe validation for optional parameters
  const hasValidBorder = border_border && typeof border_border === 'string' && border_border?.trim() !== '';
  const hasValidShadow = effect_box_shadow && typeof effect_box_shadow === 'string' && effect_box_shadow?.trim() !== '';
  const hasValidGap = layout_gap && typeof layout_gap === 'string' && layout_gap?.trim() !== '';
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width?.trim() !== '';
  const hasValidPadding = padding && typeof padding === 'string' && padding?.trim() !== '';
  const hasValidMargin = margin && typeof margin === 'string' && margin?.trim() !== '';
  const hasValidPosition = position && typeof position === 'string' && position?.trim() !== '';

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidBorder ? `${border_border}` : '',
    hasValidShadow ? `shadow-[${effect_box_shadow}]` : '',
    hasValidGap ? `gap-[${layout_gap}]` : '',
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidPadding ? `${padding}` : '',
    hasValidMargin ? `m-[${margin}]` : '',
    hasValidPosition ? position : '',
  ]?.filter(Boolean)?.join(' ');

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <button data-component-id="src\components\ui\Button.jsx:95:4" data-component-path="src\components\ui\Button.jsx" data-component-line="95" data-component-end-line="149" data-component-file="Button.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={twMerge(
        buttonClasses({ variant, size }),
        text_font_size,
        `font-${text_font_family}`,
        text_font_weight,
        text_line_height,
        `text-${text_text_align}`,
        text_color,
        fill_background_color,
        border_border_radius,
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {leftImage && (
        <img data-component-id="src\components\ui\Button.jsx:116:8" data-component-path="src\components\ui\Button.jsx" data-component-line="116" data-component-end-line="122" data-component-file="Button.jsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22mr-2%22%7D" 
          src={leftImage?.src} 
          width={leftImage?.width} 
          height={leftImage?.height}
          alt="" 
          className="mr-2"
        />
      )}
      {leftIcon && (
        <>
          {typeof leftIcon === 'object' && leftIcon?.iconName ? (
            // Font Awesome icon definition
            <Icon data-component-id="src\components\ui\Button.jsx:128:12" data-component-path="src\components\ui\Button.jsx" data-component-line="128" data-component-end-line="132" data-component-file="Button.jsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22mr-2%22%7D" 
              icon={leftIcon} 
              color={leftIconColor || "text-current"} 
              className="mr-2"
            />
          ) : (
            // Legacy class-based icon (e.g., Font Awesome 5 classes)
            <i data-component-id="src\components\ui\Button.jsx:135:12" data-component-path="src\components\ui\Button.jsx" data-component-line="135" data-component-end-line="137" data-component-file="Button.jsx" data-component-name="i" data-component-content="%7B%22elementName%22%3A%22i%22%2C%22className%22%3A%22%22%7D" 
              className={"mr-2" + " " + leftIcon?.class} 
            />
          )}
        </>
      )}
      {rightIcon && typeof rightIcon === 'object' && rightIcon?.iconName && (
        <Icon data-component-id="src\components\ui\Button.jsx:142:8" data-component-path="src\components\ui\Button.jsx" data-component-line="142" data-component-end-line="146" data-component-file="Button.jsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22ml-2%22%7D" 
          icon={rightIcon} 
          color={rightIconColor || "text-current"} 
          className="ml-2"
        />
      )}
      {children || text}
    </button>
  );
};

export default Button;